import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { fetchUserPermissions } from "../../api/permissions";
import { buildAbilityFor, AppAbility } from "../../casl/ability";
import { token } from "../../api";
import { AxiosError } from "axios";
import { Permission } from "../../utils/app-types";
import { showErrorAlert } from "../shared/alerts";
import { AbilityContext } from "../../casl/Can";
import { useIsAuthenticated } from "react-auth-kit";

/**
 * List of paths that don't require authentication
 */
const paths = [
  "/login",
  "/signup",
  "/reset-password",
  "/forgot-password",
  "/set-password",
  "/imprint",
];

/**
 * Props interface for the UserPermissions component
 */
interface UserPermissionsProps {
  children: ReactNode;
}

/**
 * UserPermissions Component
 * 
 * A higher-order component that manages user permissions and authentication state.
 * It handles:
 * - Permission fetching and ability building
 * - Authentication state management
 * - Protected route redirection
 * - Permission context provisioning
 * 
 * @component
 * @param {UserPermissionsProps} props - Component props
 * @param {ReactNode} props.children - Child components to be wrapped with permission context
 * 
 * @example
 * ```tsx
 * <UserPermissions>
 *   <YourProtectedComponent />
 * </UserPermissions>
 * ```
 */
const UserPermissions: FunctionComponent<UserPermissionsProps> = (props) => {
  const isAuthenticated = useIsAuthenticated();
  const navigate: NavigateFunction = useNavigate();

  const { children } = props;

  const [ability, setAbility] = useState<AppAbility | undefined>();

  const location = useLocation();

  /**
   * Checks if the current location is a public path that doesn't require authentication
   * 
   * @returns {boolean} True if the current path is public, false otherwise
   */
  const checkLocation = () => {
    return (
      paths.includes(location.pathname) ||
      location.pathname.startsWith("/api/auth/email/verify")
    );
  };

  const isLanding = checkLocation();

  /**
   * Fetches user permissions from the server and builds the ability object
   * 
   * @throws {Error} If permission fetching fails
   */
  const fetchPermissions = async () => {
    try {
      const permissionsData = await fetchUserPermissions(token);
      setAbility(buildAbilityFor(permissionsData.data as Permission[]));
    } catch (error: AxiosError | any) {
      const { data } = error.response;
      showErrorAlert(data.message);
    }
  };

  /**
   * Redirects unauthenticated users to the login page
   */
  const redirectUser = () => {
    const authenticated = isAuthenticated();
    if (location.pathname === "/" && authenticated) {
      navigate("/dashboard");
    }
    if (!authenticated) {
      navigate("/login");
    }
  };

  /**
   * Bootstraps the user by fetching permissions and setting up the ability context
   */
  const BootstrapUser = async () => {
    if (!isLanding) {
      if (isAuthenticated()) {
        await fetchPermissions();
      }
      redirectUser();
    }
  };

  useEffect(() => {
    BootstrapUser();
  }, [isAuthenticated()]); // Call the function with dependency on isAuthenticated to ensure it's only called once or when authentication status changes

  if (isLanding) {
    return (
      <>
        <main className="content no-margin">{children}</main>
      </>
    );
  }

  return (
    <>
      {ability && (
        <AbilityContext.Provider value={ability}>
          {children}
        </AbilityContext.Provider>
      )}
    </>
  );
};

export default UserPermissions;
